/**
 * 本文HTMLを公開ページに描くための変換
 *
 * 目的: 「サニタイズ → 動画の埋め込みに差し替え」の順を1か所に閉じ込める。
 *
 * 経緯: 本文の描画は公開ページ・管理画面のプレビュー・会員紹介ページに分かれており、
 * サニタイザの取り違えで「会員のHTMLを運営記事用の緩いサニタイザで描く」事故が
 * 起きやすい（PostBodyRenderer が isMemberPost を必須引数にしているのも同じ理由）。
 * 動画の差し替えが加わって手順が2段になったので、順番ごとここに集める。
 *
 * 差し替えをサニタイズ関数の中に入れない理由:
 * - MemberProfileForm は保存前に sanitizeMemberProfileHtml を通してからDBへ入れている。
 *   サニタイズ関数が iframe を返すようになると、DBに iframe が保存されてしまう
 */

import {
  sanitizeHtmlContent,
  sanitizeMemberPostHtml,
  sanitizeMemberProfileHtml,
} from '@/lib/sanitizeHtml';
import { renderYouTubeEmbeds } from '@/lib/youtubeEmbed';

/** 本文の書き手ごとの許可範囲 */
export type BodyVariant =
  /** 運営が書いたブログ記事。見出し・引用・コードまで許す */
  | 'adminPost'
  /** 会員が書いたブログ記事。h1 と pre/code は許さない */
  | 'memberPost'
  /** 会員紹介ページの本文。太字・リンク・箇条書き・画像だけ */
  | 'memberProfile';

const SANITIZERS: Record<BodyVariant, (html: string) => string> = {
  adminPost: sanitizeHtmlContent,
  memberPost: sanitizeMemberPostHtml,
  memberProfile: sanitizeMemberProfileHtml,
};

/**
 * 本文HTMLを、公開ページにそのまま流し込める形にする
 *
 * 引数:
 * - html: 保存されている本文HTML
 * - variant: 誰が書いた本文か。省略できないのは、既定値を持たせると渡し忘れた
 *   呼び出し側が黙って緩い方で会員のHTMLを描いてしまうため
 *
 * 戻り値: サニタイズ済み＋動画を埋め込みに差し替えたHTML
 */
export function renderBodyHtml(html: string, variant: BodyVariant): string {
  return renderYouTubeEmbeds(SANITIZERS[variant](html ?? ''));
}
